/**
 * Theme Helper to dynamically apply Organization Primary and Secondary colors
 * to CSS variables in the DOM (:root).
 */

// Helper to convert hex (#RRGGBB or #RGB) to { r, g, b }
export const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  if (!hex) return null;
  let cleanHex = hex.replace("#", "").trim();
  if (cleanHex.length === 3) {
    cleanHex = cleanHex
      .split("")
      .map((c) => c + c)
      .join("");
  }
  if (cleanHex.length !== 6) return null;
  const num = parseInt(cleanHex, 16);
  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255,
  };
};

// Helper to lighten/tint a color by a percentage (0 to 100)
export const lightenColor = (hex: string, percent: number): string => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const r = Math.min(255, Math.round(rgb.r + (255 - rgb.r) * (percent / 100)));
  const g = Math.min(255, Math.round(rgb.g + (255 - rgb.g) * (percent / 100)));
  const b = Math.min(255, Math.round(rgb.b + (255 - rgb.b) * (percent / 100)));
  return `rgb(${r}, ${g}, ${b})`;
};

// Helper to determine if a color is light or dark based on perceived brightness (YIQ formula)
export const isColorLight = (hex: string): boolean => {
  const rgb = hexToRgb(hex);
  if (!rgb) return false;
  // Perceived brightness formula (YIQ standard: 0 to 255)
  const yiq = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
  return yiq >= 140;
};

// Helper to get contrast text color (dark for light backgrounds, white for dark backgrounds)
export const getContrastTextColor = (hex: string): string => {
  return isColorLight(hex) ? "#212529" : "#ffffff";
};

/**
 * Apply Organization theme colors to the document root (:root)
 * @param primary Primary brand color (e.g. #2ca9b8)
 * @param secondary Secondary brand color (e.g. #939597)
 */
export const applyOrganizationTheme = (primary?: string | null, secondary?: string | null) => {
  const primaryColor =
    primary && /^#[0-9A-Fa-f]{3,8}$/.test(primary)
      ? primary
      : localStorage.getItem("org_primary_color") || "#2ca9b8";

  const secondaryColor =
    secondary && /^#[0-9A-Fa-f]{3,8}$/.test(secondary)
      ? secondary
      : localStorage.getItem("org_secondary_color") || "#939597";

  try {
    localStorage.setItem("org_primary_color", primaryColor);
    localStorage.setItem("org_secondary_color", secondaryColor);
  } catch {}

  const root = document.documentElement;
  if (!root) return;

  // Set primary variables
  root.style.setProperty("--primary-color", primaryColor);
  const primaryRgb = hexToRgb(primaryColor);
  if (primaryRgb) {
    root.style.setProperty("--primary-color-rgb", `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`);
  }
  root.style.setProperty("--primary-light-5", lightenColor(primaryColor, 5));
  root.style.setProperty("--primary-light-25", lightenColor(primaryColor, 25));
  root.style.setProperty("--primary-light-45", lightenColor(primaryColor, 45));
  root.style.setProperty("--primary-light-60", lightenColor(primaryColor, 60));
  root.style.setProperty("--primary-light-80", lightenColor(primaryColor, 80));

  // Determine contrast for primary color (dark background -> white icon, light background -> dark icon)
  const isPrimaryLight = isColorLight(primaryColor);
  const primaryContrastColor = isPrimaryLight ? "#212529" : "#ffffff";
  const searchIconFilter = isPrimaryLight
    ? "grayscale(100%) brightness(0.2)"
    : "brightness(0) invert(1)";

  root.style.setProperty("--primary-contrast-color", primaryContrastColor);
  root.style.setProperty("--search-icon-filter", searchIconFilter);
  root.style.setProperty("--search-icon-color", primaryContrastColor);

  // Set secondary variables
  root.style.setProperty("--secondary-color", secondaryColor);
  const secondaryRgb = hexToRgb(secondaryColor);
  if (secondaryRgb) {
    root.style.setProperty("--secondary-color-rgb", `${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}`);
  }
  root.style.setProperty("--secondary-light-5", lightenColor(secondaryColor, 5));
  root.style.setProperty("--secondary-light-25", lightenColor(secondaryColor, 25));

  const isSecondaryLight = isColorLight(secondaryColor);
  root.style.setProperty("--secondary-contrast-color", isSecondaryLight ? "#212529" : "#ffffff");
};

// Auto-bootstrap on initial script execution
try {
  applyOrganizationTheme();
} catch {}

